import React, { useContext } from 'react';
import { PostData, RulesNameInfoBox } from './types';
import { Link } from 'react-router-dom';
import { getInfoBoxErrors } from './instagramPublishingRules';
import { BeaconContext } from '../../../../providers/BeaconProvider';

const INSTALL_MOBILE_APP = (
  <>
    You may wish to{' '}
    <Link to={'/settings/mobile'} target={'_blank'}>
      install our mobile app
    </Link>{' '}
    to make publishing easier.
  </>
);
const NOTIFY_MOBILE = <>You will receive a mobile or email notification when this post is due to be published. </>;
const NOTIFY_EMAIL = (
  <>You will receive an email notification when this post is due to be published. {INSTALL_MOBILE_APP}</>
);
export const MessageInfo = (props: {
  key: RulesNameInfoBox;
  instaProfilePostData: PostData;
  hasMobile?: boolean;
}): JSX.Element => {
  const { key, instaProfilePostData, hasMobile } = props;
  const reminderVariable = hasMobile ? NOTIFY_MOBILE : NOTIFY_EMAIL;
  const alreadyHasReminderVariable = getInfoBoxErrors(['forceReminder', 'manualStoryA'], instaProfilePostData).length;
  const { beacon } = useContext(BeaconContext);
  if (key === 'creatorStoryManual') {
    return (
      <>
        <p>The Instagram API does not currently support direct-publishing of Stories for Creator profiles.</p>
        {!alreadyHasReminderVariable && <p>{reminderVariable}</p>}
      </>
    );
  } else if (key === 'videoUnder3Sec') {
    return (
      <>
        <p>This post contains a video that is too short to be published to Instagram directly.</p>
      </>
    );
  } else if (key === 'directVideoOver15Mins') {
    return (
      <>
        <p>This post contains a video that is too long to be published to Instagram directly.</p>
        <p>Instagram video posts are limited to the first 15 minutes.</p>
      </>
    );
  } else if (key === 'directVideoOver15MinsWide') {
    return (
      <>
        <p>This post contains a video that is too long and too wide to be published to Instagram directly.</p>
        <p>Instagram video posts are limited to the first 15 minutes.</p>
        <p>
          Direct-published Instagram video Stories wider than 1.91:1 will have the sides cut off. You may wish to
          publish it via a Reminder, where you will be able to resize the video to fit.
        </p>
      </>
    );
  } else if (key === 'directVideoOver15MinsTall') {
    return (
      <>
        <p>This post contains a video that is too long and too tall to be published to Instagram directly.</p>
        <p>Instagram video posts are limited to the first 15 minutes.</p>
        <p>
          Direct-published Instagram video Stories taller than 9:16 will have the top and bottom cut off. You may wish
          to publish it via a Reminder, where you will be able to resize the video to fit.
        </p>
      </>
    );
  } else if (key === 'forceReminder') {
    return (
      <>
        <p>{reminderVariable}</p>
      </>
    );
  } else if (key === 'manualVideoOver15MinsA') {
    return (
      <>
        <p>Only the first 15 minutes will be published.</p>
      </>
    );
  } else if (key === 'manualVideoOver15MinsB') {
    return (
      <>
        <p>Instagram only allows feed/reel videos up to 15 mins</p>
      </>
    );
  } else if (key === 'videoStoryOver60Sec') {
    return (
      <>
        <p>This post contains a video that is too long to be published to Instagram directly.</p>
      </>
    );
  } else if (key === 'directStoryNoCaptions') {
    return (
      <>
        <p>Instagram Stories that are published directly will not include any captions.</p>
      </>
    );
  } else if (key === 'cropDirectImageStoryWide') {
    return (
      <>
        <p>
          Direct-published Instagram image Stories wider than 1.91:1 will have the sides cut off. You may wish to
          publish it via a Reminder, where you will be able to resize the image to fit.
        </p>
      </>
    );
  } else if (key === 'cropDirectImageStoryTall') {
    return (
      <>
        <p>
          Direct-published Instagram image Stories taller than 9:16 will have the top and bottom cut off. You may wish
          to publish it via a Reminder, where you will be able to resize the image to fit.
        </p>
      </>
    );
  } else if (key === 'cropDirectImageStoryMidTall') {
    return (
      <>
        <p>
          Direct-published Instagram image Stories between 4:5 and 9:16 tall will have the sides cut off. You may wish
          to publish it via a Reminder, where you will be able to resize the image to fit.
        </p>
      </>
    );
  } else if (key === 'cropDirectVideoStoryWide') {
    return (
      <>
        <p>
          Direct-published Instagram video Stories wider than 1.91:1 will have the sides cut off. You may wish to
          publish it via a Reminder, where you will be able to resize the video to fit.
        </p>
      </>
    );
  } else if (key === 'cropDirectVideoStoryTall') {
    return (
      <>
        <p>
          Direct-published Instagram video Stories taller than 9:16 will have the top and bottom cut off. You may wish
          to publish it via a Reminder, where you will be able to resize the video to fit.
        </p>
      </>
    );
  } else if (key === 'cropDirectVideoStoryMidTall') {
    return (
      <>
        <p>
          Direct-published Instagram video Stories between 4:5 and 9:16 tall will have the sides cut off. You may wish
          to publish it via a Reminder, where you will be able to resize the image to fit.
        </p>
      </>
    );
  } else if (key === 'manualStoryA') {
    return (
      <>
        <p>{reminderVariable}</p>
      </>
    );
  } else if (key === 'tallFeedVideo') {
    return (
      <>
        <p>Instagram Feed/Reel videos taller than 9:16 will have the top and bottom cut off.</p>
      </>
    );
  } else if (key === 'wideManualFeedVideoA' || key === 'wideManualFeedVideoB') {
    return (
      <>
        <p>
          Manually-published Instagram Feed videos wider than 1.91:1 will have the sides cut off. You should select
          &quot;Reel&quot; in the Instagram mobile app to publish wider videos.
        </p>
      </>
    );
  } else if (key === 'MultiImageStoryManual') {
    return (
      <>
        <p>This post cannot be published to Instagram directly, as their API only supports single-image stories.</p>
        <p>{reminderVariable}</p>
      </>
    );
  } else if (key === 'MultiImageFeedCrop') {
    return (
      <>
        <p>
          All images in a Carousel Post will be cropped to the size of the first image.{' '}
          <a onClick={() => beacon('article', '721', { type: 'modal' })}>Learn more</a>.
        </p>
      </>
    );
  } else if (key === 'EnableDirectPublishNoMobile') {
    return (
      <>
        {!alreadyHasReminderVariable && <p>{reminderVariable}</p>}
        <p>
          You may wish to{' '}
          <Link to={'/settings/profiles'} target={'_blank'}>
            enable direct publishing
          </Link>{' '}
          on the profile(s) or{' '}
          <Link to={'/settings/mobile'} target={'_blank'}>
            download our mobile app
          </Link>{' '}
          to make publishing easier.
        </p>
      </>
    );
  } else if (key === 'EnableDirectPublishHaveMobile') {
    return (
      <>
        {!alreadyHasReminderVariable && <p>{reminderVariable}</p>}
        <p>
          You may wish to{' '}
          <Link to={'/settings/profiles'} target={'_blank'}>
            {' '}
            enable direct publishing
          </Link>{' '}
          on the profile(s).
        </p>
      </>
    );
  } else if (key === 'DirectAndMobileReminder') {
    return (
      <>
        <p>
          This post will be published directly where possible. You will also receive a mobile notification for Instagram
          profiles that do not have{' '}
          <Link to={'/settings/profiles'} target={'_blank'}>
            direct publishing enabled
          </Link>
          .
        </p>
      </>
    );
  } else if (key === 'DirectAndEmailReminder') {
    return (
      <>
        <p>
          This post will be published directly where possible. You will also receive an email notification for Instagram
          profiles that do not have{' '}
          <Link to={'/settings/profiles'} target={'_blank'}>
            direct publishing enabled
          </Link>
          . You may wish to download our mobile app to make publishing easier.
        </p>
      </>
    );
  }
  return <></>;
};
